import { useLayoutEffect, useRef, useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, IconButton, Tooltip } from "@mui/material";
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  paletteVar,
  paletteVarAlpha,
  Typography,
} from "@platform/design-system";

import { TIER_COLORS } from "./constants";
import { fmtAxisCurrency, fmtCurrency, fmtCurrencyExact } from "./formatters";

import type { CarrierTier } from "../types";

const MIN_LABEL_WIDTH = 124;
const MAX_LABEL_WIDTH = 180;
const LABEL_WIDTH_RATIO = 0.33;
const LABEL_TICK_GAP = 8;
const LABEL_MAX_BOX_HEIGHT = 40;
const MIN_BAR_SIZE = 16;
const MAX_BAR_SIZE = 32;
// Vertical space reserved per carrier row. Sized for a (potentially 2-line)
// wrapped carrier-name label rather than the bar, so labels never clip — the
// chart grows taller instead of squeezing rows.
const MIN_ROW_HEIGHT = 32;
const CHART_MARGIN_TOP = 4;
const CHART_MARGIN_BOTTOM = 24;
const CHART_VERTICAL_MARGINS = CHART_MARGIN_TOP + CHART_MARGIN_BOTTOM;
const SMALL_CURRENCY_AXIS_STEP = 10;
const SMALL_CURRENCY_AXIS_MAX = 100;
const AXIS_TICK_COUNT = 4;
const NICE_AXIS_MULTIPLIERS = [1, 2, 5, 10] as const;
const TEXT_SECONDARY_COLOR = "text.secondary";
const TOOLTIP_MOUSE_OFFSET = 12;
const TOOLTIP_Z_INDEX = 2;

type CarrierPremiumDatum = { name: string; amount: number; tier: CarrierTier };

type HorizontalBarChartProps = {
  title: string;
  description?: string;
  data: CarrierPremiumDatum[];
};

type YAxisTickProps = {
  x?: number;
  y?: number;
  payload?: { value?: string };
  labelWidth: number;
  bandHeight: number;
};

type ChartTooltipProps = {
  active?: boolean;
  payload?: { payload?: CarrierPremiumDatum }[];
};

const calculateLabelWidth = (containerWidth: number): number =>
  Math.min(
    MAX_LABEL_WIDTH,
    Math.max(MIN_LABEL_WIDTH, containerWidth * LABEL_WIDTH_RATIO),
  );

const calculateSmallCurrencyTicks = (maxValue: number): number[] => {
  const axisMax =
    Math.ceil(maxValue / SMALL_CURRENCY_AXIS_STEP) * SMALL_CURRENCY_AXIS_STEP;

  return Array.from(
    { length: axisMax / SMALL_CURRENCY_AXIS_STEP + 1 },
    (_, index) => index * SMALL_CURRENCY_AXIS_STEP,
  );
};

const calculateNiceAxisStep = (maxValue: number): number => {
  const roughStep = maxValue / (AXIS_TICK_COUNT - 1);
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const multiplier = NICE_AXIS_MULTIPLIERS.find(
    (candidate) => roughStep <= candidate * magnitude,
  );

  return (multiplier ?? 10) * magnitude;
};

const calculateLargeCurrencyTicks = (maxValue: number): number[] => {
  const tickStep = calculateNiceAxisStep(maxValue);
  const axisMax = Math.ceil(maxValue / tickStep) * tickStep;

  return Array.from(
    { length: Math.floor(axisMax / tickStep) + 1 },
    (_, index) => index * tickStep,
  );
};

const calculateCurrencyTicks = (maxValue: number): number[] => {
  if (maxValue <= 0) return [0, SMALL_CURRENCY_AXIS_STEP];

  if (maxValue <= SMALL_CURRENCY_AXIS_MAX) {
    return calculateSmallCurrencyTicks(maxValue);
  }

  return calculateLargeCurrencyTicks(maxValue);
};

const CarrierNameTick = ({
  x = 0,
  y = 0,
  payload,
  labelWidth,
  bandHeight,
}: YAxisTickProps): React.JSX.Element => {
  const label = payload?.value ?? "";
  const boxHeight = Math.min(bandHeight, LABEL_MAX_BOX_HEIGHT);

  return (
    <foreignObject
      // Anchor the label box's right edge to the tick coordinate `x`. recharts
      // places that anchor at (YAxis width - its ~8px tick offset); the YAxis
      // reserves labelWidth + LABEL_TICK_GAP, so the box lands at x=0 (flush to
      // the SVG left edge) with the gap falling between the text and the bars.
      // Do NOT also subtract the gap here, or the box is pushed off-screen left.
      x={x - labelWidth}
      y={y - boxHeight / 2}
      width={labelWidth}
      height={boxHeight}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          height: "100%",
          justifyContent: "flex-end",
          width: "100%",
        }}
      >
        <Typography
          title={label}
          variant="caption"
          sx={{
            display: "-webkit-box",
            overflow: "hidden",
            textAlign: "right",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            wordBreak: "break-word",
          }}
          color="textSecondary"
        >
          {label}
        </Typography>
      </Box>
    </foreignObject>
  );
};

const ChartTooltip = ({
  active,
  payload,
}: ChartTooltipProps): React.JSX.Element | null => {
  const datum = payload?.[0]?.payload;

  if (!active || !datum) return null;

  return (
    <Box
      sx={{
        backgroundColor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        boxShadow: 2,
        maxWidth: 260,
        p: 1.25,
        position: "relative",
        zIndex: TOOLTIP_Z_INDEX,
      }}
    >
      <Typography variant="body-medium" sx={{ mb: 0.75 }}>
        <strong>{datum.name}</strong>
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
        <Box
          sx={{
            bgcolor: TIER_COLORS[datum.tier],
            borderRadius: "50%",
            height: 10,
            width: 10,
          }}
        />
        <Typography variant="caption" color="textSecondary">
          {datum.tier}
        </Typography>
      </Box>
      <Typography variant="body-medium">
        <strong>{fmtCurrency(datum.amount)}</strong>
      </Typography>
      <Typography variant="caption" color="textSecondary">
        Exact premium: {fmtCurrencyExact(datum.amount)}
      </Typography>
    </Box>
  );
};

function HorizontalBarChart({
  title,
  description,
  data,
}: HorizontalBarChartProps): React.JSX.Element {
  const maxVal = Math.max(...data.map((d) => d.amount), 0);
  const currencyTicks = calculateCurrencyTicks(maxVal);
  const axisMax = currencyTicks[currencyTicks.length - 1];
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const chartBoxRef = useRef<HTMLDivElement | null>(null);
  const [labelWidth, setLabelWidth] = useState(MIN_LABEL_WIDTH);
  const [chartBoxHeight, setChartBoxHeight] = useState(0);

  const plotHeight = Math.max(0, chartBoxHeight - CHART_VERTICAL_MARGINS);
  const bandHeight = data.length > 0 ? plotHeight / data.length : 0;
  const barSize = Math.min(MAX_BAR_SIZE, Math.max(MIN_BAR_SIZE, bandHeight));

  useLayoutEffect(() => {
    const chartContainer = chartContainerRef.current;
    if (!chartContainer) return undefined;

    const updateMeasurements = (): void => {
      setLabelWidth(calculateLabelWidth(chartContainer.clientWidth));
      const chartBox = chartBoxRef.current;
      if (chartBox) setChartBoxHeight(chartBox.clientHeight);
    };

    updateMeasurements();

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver(updateMeasurements);
      resizeObserver.observe(chartContainer);
      const chartBox = chartBoxRef.current;
      if (chartBox) resizeObserver.observe(chartBox);

      return () => resizeObserver.disconnect();
    }

    if (typeof window !== "undefined") {
      window.addEventListener("resize", updateMeasurements);

      return () => window.removeEventListener("resize", updateMeasurements);
    }

    return undefined;
  }, [data.length]);

  return (
    <Box
      ref={chartContainerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minWidth: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
        <Typography>
          <strong>{title}</strong>
        </Typography>
        {description && (
          <Tooltip title={description}>
            <IconButton
              aria-label={`${title} definition`}
              size="small"
              sx={{ p: 0.25, color: TEXT_SECONDARY_COLOR }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {data.length === 0 ? (
        <Typography variant="body-medium" color="textSecondary">
          No carrier premium data.
        </Typography>
      ) : (
        <Box
          ref={chartBoxRef}
          sx={{
            flex: 1,
            minHeight: data.length * MIN_ROW_HEIGHT + CHART_VERTICAL_MARGINS,
            minWidth: 0,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={data}
              margin={{
                left: 0,
                right: 12,
                top: CHART_MARGIN_TOP,
                bottom: CHART_MARGIN_BOTTOM,
              }}
            >
              <XAxis
                type="number"
                domain={[0, axisMax]}
                ticks={currencyTicks}
                tickFormatter={fmtAxisCurrency}
                tick={{ fontSize: 11, fill: "rgba(0,0,0,0.65)" }}
                interval={0}
                minTickGap={12}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                // Reserve the label box width plus the tick gap, so the axis line sits at
                // (labelWidth + LABEL_TICK_GAP) from the left edge. CarrierNameTick anchors
                // the label box at (x - labelWidth), so it lands flush at x=0 and the gap
                // falls between the label text and the bars.
                width={labelWidth + LABEL_TICK_GAP}
                interval={0}
                tick={
                  <CarrierNameTick
                    labelWidth={labelWidth}
                    bandHeight={bandHeight}
                  />
                }
                axisLine={false}
                tickLine={false}
              />
              <RechartsTooltip
                allowEscapeViewBox={{ x: true, y: true }}
                content={<ChartTooltip />}
                cursor={{
                  fill: paletteVarAlpha(
                    paletteVar("text-primary", "#00344F"),
                    0.06,
                  ),
                }}
                isAnimationActive={false}
                offset={TOOLTIP_MOUSE_OFFSET}
                wrapperStyle={{
                  pointerEvents: "none",
                  zIndex: TOOLTIP_Z_INDEX,
                }}
              />
              <Bar dataKey="amount" barSize={barSize} radius={[0, 4, 4, 0]}>
                {data.map((entry) => (
                  <Cell
                    key={`${entry.name}-${entry.tier}-${entry.amount}`}
                    fill={TIER_COLORS[entry.tier]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
}

export { HorizontalBarChart };
